import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { authAPI, setCsrfToken, clearCsrfToken, getCsrfToken } from './api';

const AuthContext = createContext(null);

// Token refresh interval (10 minutes)
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    
    // Listen for user interactions
    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('mousemove', updateActivity);
    
    return () => {
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('mousemove', updateActivity);
    };
  }, []);

  // Check session on mount - Cookie-based auth
  useEffect(() => {
    checkSession();
  }, []);

  // Auto-refresh CSRF token periodically when user is active
  useEffect(() => {
    if (user) {
      // Start refresh interval
      refreshIntervalRef.current = setInterval(async () => {
        // Only refresh if user was active in the last 30 minutes
        const timeSinceActivity = Date.now() - lastActivityRef.current;
        if (timeSinceActivity < 30 * 60 * 1000) {
          console.log('Auto-refreshing CSRF token...');
          await refreshCsrfToken();
        }
      }, TOKEN_REFRESH_INTERVAL);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [user]);

  const checkSession = async () => {
    try {
      // Try to get user data - cookie will be sent automatically
      const response = await authAPI.getMe();
      setUser(response.data);
      
      // Refresh CSRF token on session check
      try {
        const csrfResponse = await authAPI.getCsrfToken();
        setCsrfToken(csrfResponse.data.csrf_token);
      } catch (csrfError) {
        console.warn('Could not refresh CSRF token:', csrfError);
      }
    } catch (error) {
      // Not authenticated or session expired
      setUser(null);
      clearCsrfToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      // Cookie is set automatically by the server
      // Response contains user data and CSRF token
      setUser(response.data.user);
      
      // Store CSRF token
      if (response.data.csrf_token) {
        setCsrfToken(response.data.csrf_token);
      }
      
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      clearCsrfToken();
      // BrowserRouter-compatible redirect
      window.location.href = '/login';
    }
  }, []);

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      // Cookie is set automatically by the server
      setUser(response.data.user);
      
      // Store CSRF token
      if (response.data.csrf_token) {
        setCsrfToken(response.data.csrf_token);
      }
      
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  const updateUser = (updatedData) => {
    if (user) {
      setUser({ ...user, ...updatedData });
    }
  };

  // Refresh CSRF token (useful after long idle periods)
  const refreshCsrfToken = async () => {
    try {
      const response = await authAPI.getCsrfToken();
      setCsrfToken(response.data.csrf_token);
      return true;
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    updateUser,
    checkSession,
    refreshCsrfToken,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'licensed',
    isEmployee: user?.role === 'employee',
    isMainAdmin: user?.role === 'admin' && (user?.tenant_id === 'main' || !user?.tenant_id),
    isTenantAdmin: user?.role === 'admin' && user?.tenant_id && user?.tenant_id !== 'main',
    hasCsrfToken: !!getCsrfToken(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
